import React from "react";
import { Shield, Leaf } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
    <div className="mt-3 text-foreground/80 leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPage = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="flex items-center gap-3">
      <span className="grid place-items-center h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <Shield className="h-5 w-5" />
      </span>
      <span className="text-xs uppercase tracking-widest text-muted-foreground">Legal</span>
    </div>
    <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight">Privacy Policy</h1>
    <p className="mt-3 text-muted-foreground">Last updated: February 23, 2026</p>

    <p className="mt-8 leading-relaxed">
      At Ecowallet, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect
      your personal information when you use our sustainable shopping companion platform.
    </p>

    <Section title="1. Information We Collect">
      <p>We collect the following types of information:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li><strong>Account information:</strong> name, email address, and password (stored as an encrypted hash).</li>
        <li><strong>Shopping activity:</strong> products viewed, purchases made through affiliate links, Green Points earned, and cashback accumulated.</li>
        <li><strong>Sustainability data:</strong> the environmental impact metrics associated with your purchases (carbon saved, water saved, plastic avoided).</li>
        <li><strong>Technical information:</strong> device type, browser, IP address, and usage analytics.</li>
      </ul>
    </Section>

    <Section title="2. How We Use Your Data">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>To operate the Ecowallet platform and personalize product recommendations.</li>
        <li>To calculate and credit Green Points, cashback, and impact metrics.</li>
        <li>To provide leaderboards and gamified experiences (only aggregate/username visible to others).</li>
        <li>To communicate with you about your account, redemptions, and important updates.</li>
        <li>To improve our sustainability scoring methodology and product catalog.</li>
      </ul>
    </Section>

    <Section title="3. Data Sharing">
      <p>
        We do not sell your personal data. We share limited information only with:
      </p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Affiliate merchants (Amazon, Flipkart, Myntra, etc.) when you click a Buy Now link — only anonymized click IDs required for cashback tracking.</li>
        <li>Payment providers for reward redemption.</li>
        <li>Legal authorities when required by law.</li>
      </ul>
    </Section>

    <Section title="4. Cookies & Tracking">
      <p>
        We use essential cookies for authentication (JWT tokens stored as httpOnly cookies) and analytical cookies to
        improve the platform. You can manage cookie preferences from your browser settings.
      </p>
    </Section>

    <Section title="5. Data Security">
      <p>
        Passwords are hashed using bcrypt. All traffic is encrypted with TLS. We follow industry-standard security
        practices, but no system is 100% secure — please use a strong, unique password.
      </p>
    </Section>

    <Section title="6. Your Rights">
      <p>You may at any time:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Access, update, or delete your account data.</li>
        <li>Withdraw consent to marketing communications.</li>
        <li>Request a copy of your data or ask us to erase it.</li>
      </ul>
      <p>Contact us at <a href="mailto:privacy@ecowallet.in" className="text-primary hover:underline">privacy@ecowallet.in</a> to exercise these rights.</p>
    </Section>

    <Section title="7. Changes to this Policy">
      <p>
        We may update this policy periodically. Material changes will be communicated via email or a notice on the
        platform.
      </p>
    </Section>

    <Section title="8. Contact">
      <p>
        Questions? Email <a href="mailto:privacy@ecowallet.in" className="text-primary hover:underline">privacy@ecowallet.in</a>.
      </p>
    </Section>

    <div className="mt-14 card-premium p-6 flex items-center gap-4">
      <Leaf className="h-6 w-6 text-emerald-600" />
      <p className="text-sm text-muted-foreground">
        We believe sustainable shopping and data privacy go hand in hand — both are about respect.
      </p>
    </div>
  </div>
);

export default PrivacyPage;
